// Exporta la animática (todas las páginas/frames en orden) como video WebM,
// usando únicamente APIs nativas del navegador (Canvas + MediaRecorder) —
// sin dependencias nuevas, así que no requiere ffmpeg ni instalar nada.
//
// Limitación inherente a este enfoque: la grabación ocurre en tiempo real,
// o sea que exportar un storyboard de 30s de reproducción tarda ~30s.

import { DEFAULT_PANEL_DURATION } from "./storyboardIO";

const MIME_CANDIDATES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
];

export const isVideoExportSupported = () =>
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined" &&
  typeof document.createElement("canvas").captureStream === "function";

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) || null;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo cargar una imagen del storyboard."));
    img.src = src;
  });

// Dibuja `img` cubriendo todo el canvas (equivalente a object-fit: cover).
const drawCover = (ctx, img, width, height) => {
  const imgRatio = img.width / img.height;
  const targetRatio = width / height;
  let sx, sy, sw, sh;

  if (imgRatio > targetRatio) {
    sh = img.height;
    sw = sh * targetRatio;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / targetRatio;
    sx = 0;
    sy = (img.height - sh) / 2;
  }

  ctx.fillStyle = "#0e0e0e";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(testLine).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const drawCaption = (ctx, text, width, height) => {
  if (!text) return;
  const padding = 28;
  const lineHeight = 34;

  ctx.font = "28px Inter, sans-serif";
  const lines = wrapText(ctx, text, width - padding * 2).slice(0, 3);
  if (lines.length === 0) return;

  const boxHeight = lines.length * lineHeight + padding;
  ctx.fillStyle = "rgba(19, 19, 19, 0.78)";
  ctx.fillRect(0, height - boxHeight, width, boxHeight);

  ctx.fillStyle = "#e5e2e1";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  lines.forEach((line, i) => {
    const y = height - padding - (lines.length - 1 - i) * lineHeight;
    ctx.fillText(line, width / 2, y);
  });
};

export const exportStoryboardVideo = async (
  storyboard,
  { width = 1280, height = 720, fps = 30, onProgress } = {}
) => {
  if (!isVideoExportSupported()) {
    throw new Error(
      "Este navegador no soporta exportar video (falta MediaRecorder o captureStream). Probá con Chrome o Firefox actualizados."
    );
  }

  const mimeType = pickMimeType();
  if (!mimeType) {
    throw new Error("No se encontró un códec de video (WebM) soportado en este navegador.");
  }

  const panels = storyboard.flatMap((page) => page.panels);
  if (panels.length === 0) {
    throw new Error("No hay frames para exportar.");
  }

  const images = await Promise.all(panels.map((panel) => loadImage(panel.image)));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  const stream = canvas.captureStream(fps);
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5_000_000 });
  const chunks = [];
  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  };

  const stopped = new Promise((resolve) => {
    recorder.onstop = resolve;
  });

  // Primer frame dibujado antes de arrancar a grabar, para no capturar un
  // instante de canvas vacío.
  drawCover(ctx, images[0], width, height);
  drawCaption(ctx, panels[0].text, width, height);

  recorder.start();

  for (let i = 0; i < panels.length; i++) {
    const panel = panels[i];
    drawCover(ctx, images[i], width, height);
    drawCaption(ctx, panel.text, width, height);
    onProgress?.(i + 1, panels.length);

    const durationMs = (panel.duration || DEFAULT_PANEL_DURATION) * 1000;
    await new Promise((resolve) => setTimeout(resolve, durationMs));
  }

  recorder.stop();
  await stopped;

  const blob = new Blob(chunks, { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "storyboard.webm";
  a.click();
  URL.revokeObjectURL(url);
};
